<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">预约列表</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ stats.total }}</b> 条</span>
        <span class="divider">|</span>
        <span>待补录 <b>{{ stats.pending }}</b></span>
        <span class="divider">|</span>
        <span>预约中 <b>{{ stats.ongoing }}</b></span>
        <span class="divider">|</span>
        <span class="text-warning">未分配人员 <b>{{ stats.unassigned }}</b></span>
      </div>
    </div>

    <!-- 筛选 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" size="small" class="search-form" @submit.native.prevent>
          <el-form-item label="关键字">
            <el-input
              v-model="query.keyword"
              placeholder="姓名 / 紧急联系人 / 联系电话"
              clearable
              style="width: 200px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="预约类型">
            <!-- 类型可扩展：下列是已知类型快捷项，也能直接输入数字按新类型筛 -->
            <el-select
              v-model="query.type"
              placeholder="全部"
              clearable
              filterable
              allow-create
              default-first-option
              style="width: 120px"
            >
              <el-option v-for="t in APPOINTMENT_TYPE" :key="t.value" :label="t.label" :value="t.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 150px">
              <el-option v-for="s in APPOINTMENT_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="接送人员">
            <el-select v-model="query.service_id" placeholder="全部" clearable style="width: 140px">
              <el-option label="未分配" :value="0" />
              <el-option v-for="p in staffList" :key="p.id" :label="p.name" :value="p.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="预约日期">
            <el-date-picker
              v-model="query.dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              style="width: 250px"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button icon="el-icon-download" size="small" @click="handleExport">导出列表</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" height="100%" stripe>
        <!-- 预约编号 -->
        <el-table-column label="预约编号" width="100">
          <template slot-scope="scope">
            <span class="order-no">#{{ scope.row.id }}</span>
          </template>
        </el-table-column>
        <!-- 类型 -->
        <el-table-column label="预约类型" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            <el-tag :type="appointTypeTagType(scope.row.type)" size="mini" effect="plain">
              {{ scope.row.type_text || appointTypeText(scope.row.type) }}
            </el-tag>
            <span v-if="scope.row.ref_id" class="cell-sub">{{ scope.row.ref_name || '—' }}</span>
          </template>
        </el-table-column>
        <!-- 预约人 -->
        <el-table-column label="预约人" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="principal">{{ scope.row.name }}</span>
            <span class="cell-muted">{{ genderText(scope.row.gender) }} · {{ scope.row.people_count }} 人</span>
          </template>
        </el-table-column>
        <!-- 预约日期 -->
        <el-table-column label="预约日期" width="120">
          <template slot-scope="scope">{{ scope.row.appoint_date }}</template>
        </el-table-column>
        <!-- 到达时间 -->
        <el-table-column label="到达时间" width="160">
          <template slot-scope="scope">
            <span v-if="scope.row.arrive_time">{{ scope.row.arrive_time }}</span>
            <span v-else class="cell-muted">待补录</span>
          </template>
        </el-table-column>
        <!-- 紧急联系人 -->
        <el-table-column label="紧急联系人" min-width="170" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.emergency_name || scope.row.emergency_phone">
              {{ scope.row.emergency_name || '—' }}
              <span class="cell-muted">{{ scope.row.emergency_phone || '' }}</span>
            </span>
            <span v-else class="cell-muted">—</span>
          </template>
        </el-table-column>
        <!-- 接送人员 -->
        <el-table-column label="接送人员" min-width="140">
          <template slot-scope="scope">
            <span v-if="scope.row.service_id" class="staff-cell">
              <i class="el-icon-user" />
              {{ scope.row.service_name || '—' }}
              <span class="cell-muted">{{ scope.row.service_position || '' }}</span>
            </span>
            <span v-else class="cell-muted">未分配</span>
          </template>
        </el-table-column>
        <!-- 状态 -->
        <el-table-column label="状态" width="130" align="center">
          <template slot-scope="scope">
            <el-tag :type="appointStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ scope.row.status_text || appointStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 操作 -->
        <el-table-column label="操作" width="150" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text" size="mini" class="text-primary-btn"
              :disabled="!canAssign(scope.row)"
              @click="handleAssign(scope.row)"
            >{{ scope.row.service_id ? '改派人员' : '分配人员' }}</el-button>
            <el-dropdown trigger="click" @command="cmd => handleMore(cmd, scope.row)">
              <el-button type="text" size="mini" class="more-btn">
                更多<i class="el-icon-arrow-down el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="arrive" :disabled="!canArrive(scope.row)">补录到达时间</el-dropdown-item>
                <el-dropdown-item command="finish" :disabled="scope.row.status !== 1">标记预约完成</el-dropdown-item>
                <el-dropdown-item command="end" :disabled="scope.row.status !== 2">结束行程</el-dropdown-item>
                <el-dropdown-item command="cancel" :disabled="!canCancel(scope.row)" divided>取消预约</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="page"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <!-- 分配工作人员弹框 -->
    <el-dialog
      :title="assignRow && assignRow.service_id ? '改派工作人员' : '分配工作人员'"
      :visible.sync="assignVisible"
      width="560px"
    >
      <div v-if="assignRow" class="assign-order">
        <div class="ao-row">
          <span class="ao-label">预约</span>
          <b>#{{ assignRow.id }}</b>
          <el-tag size="mini" effect="plain">{{ assignRow.type_text || appointTypeText(assignRow.type) }}</el-tag>
          <span v-if="assignRow.ref_id" class="cell-sub">{{ assignRow.ref_name || '—' }}</span>
        </div>
        <div class="ao-row">
          <span class="ao-label">客人</span>
          <span>{{ assignRow.name }}（{{ genderText(assignRow.gender) }}·{{ assignRow.people_count }} 人）</span>
          <span class="cell-muted">{{ assignRow.appoint_date }} 到访</span>
        </div>
      </div>

      <el-input
        v-model="staffKeyword"
        size="small"
        prefix-icon="el-icon-search"
        placeholder="搜索工作人员姓名 / 电话"
        clearable
        style="margin: 14px 0 8px"
      />

      <div v-loading="staffLoading" class="staff-list">
        <div
          v-for="p in filteredStaff"
          :key="p.id"
          class="staff-item"
          :class="{ 'is-active': staffId === p.id }"
          @click="staffId = p.id"
        >
          <span class="staff-avatar">{{ (p.name || '员')[0] }}</span>
          <div class="staff-main">
            <div class="staff-name">
              {{ p.name }}
              <el-tag size="mini" effect="plain">{{ p.position_text }}</el-tag>
              <el-tag v-if="p.external" size="mini" type="info" effect="plain">外部</el-tag>
            </div>
            <div class="staff-sub">{{ p.dept_name }} · {{ p.phone }}</div>
          </div>
          <div class="staff-load">
            <span v-if="p.load" class="load-on">在途 {{ p.load }} 单</span>
            <span v-else class="load-off">空闲</span>
          </div>
        </div>
        <div v-if="!staffLoading && !filteredStaff.length" class="staff-empty">没有匹配的工作人员</div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="assignVisible = false">取 消</el-button>
        <el-button type="primary" :disabled="!staffId || submitting" :loading="submitting" @click="submitAssign">确认分配</el-button>
      </div>
    </el-dialog>

    <!-- 补录到达时间 -->
    <el-dialog title="补录到达时间" :visible.sync="arriveVisible" width="440px">
      <div v-if="arriveRow" class="arrive-tip">
        {{ arriveRow.name }}（{{ arriveRow.people_count }} 人）预约 {{ arriveRow.appoint_date }} 到访
      </div>
      <el-form label-width="80px" size="small">
        <el-form-item label="到达时间" required>
          <el-date-picker
            v-model="arriveTime"
            type="datetime"
            placeholder="选择实际到达时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            format="yyyy-MM-dd HH:mm"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <div class="arrive-note">
        <i class="el-icon-info" /> 补录后预约进入「预约中」，客人到达前请先完成接送人员分配。
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="arriveVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitArrive">确 认</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  APPOINTMENT_TYPE, APPOINTMENT_STATUS,
  appointTypeText, appointTypeTagType, appointStatusText, appointStatusTagType,
  genderText
} from '@/mock/appointment'
import {
  getAppointmentList, assignAppointment, arriveAppointment, setAppointmentStatus
} from '@/api/appointment'
import { getPersonServiceOptions } from '@/api/system'

export default {
  name: 'OrderAppointment',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      // 头部统计（后端随列表返回，全量口径，不受筛选影响）
      stats: { total: 0, pending: 0, ongoing: 0, finished: 0, canceled: 0, unassigned: 0 },
      staffList: [],
      staffLoading: false,
      submitting: false,
      query: { keyword: '', type: '', status: '', service_id: '', dateRange: null },
      assignVisible: false,
      assignRow: null,
      staffId: 0,
      staffKeyword: '',
      arriveVisible: false,
      arriveRow: null,
      arriveTime: '',
      APPOINTMENT_TYPE,
      APPOINTMENT_STATUS,
      appointTypeText,
      appointTypeTagType,
      appointStatusText,
      appointStatusTagType,
      genderText
    }
  },
  computed: {
    filteredStaff() {
      const kw = (this.staffKeyword || '').trim()
      if (!kw) return this.staffList
      return this.staffList.filter(p => (p.name || '').indexOf(kw) > -1 || (p.phone || '').indexOf(kw) > -1)
    }
  },
  created() {
    this.loadData()
    this.loadStaff()
  },
  methods: {
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.type !== '' && this.query.type !== null) params.type = this.query.type
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status
      if (this.query.service_id !== '' && this.query.service_id !== null) params.service_id = this.query.service_id
      const range = this.query.dateRange || []
      if (range.length === 2) params.date_range = range
      return getAppointmentList(params).then(res => {
        // 拦截器业务错误也 resolve，必须自判 code
        if (res.code !== 200) return
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
        this.stats = data.stats || this.stats
      }).finally(() => {
        this.loading = false
      })
    },
    // 可分配人员下拉（在职 + 接待/接送/讲解岗；后端会带各人在途预约数 load）
    loadStaff() {
      this.staffLoading = true
      return getPersonServiceOptions().then(res => {
        if (res.code !== 200) return
        this.staffList = (res.data && res.data.list) || []
      }).finally(() => {
        this.staffLoading = false
      })
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', type: '', status: '', service_id: '', dateRange: null }
      this.page = 1
      this.loadData()
    },
    handlePageChange(current) {
      this.page = current
      this.loadData()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.loadData()
    },
    // 已取消/已结束的预约不再分配人员
    canAssign(row) {
      return [0, 1, 2].indexOf(row.status) > -1
    },
    // 还没补录到达时间，且预约未结束/未取消
    canArrive(row) {
      return !row.arrive_time && row.status !== 4 && row.status !== 3
    },
    canCancel(row) {
      return [0, 1, 2].indexOf(row.status) > -1
    },
    handleAssign(row) {
      this.assignRow = row
      this.staffId = row.service_id || 0
      this.staffKeyword = ''
      this.assignVisible = true
      if (!this.staffList.length) this.loadStaff()
    },
    submitAssign() {
      if (!this.staffId || this.submitting) return
      this.submitting = true
      assignAppointment(this.assignRow.id, { service_id: this.staffId }).then(res => {
        if (res.code !== 200) return
        const data = res.data || {}
        this.$message.success(`已分配给 ${data.service_name}（${data.service_position}）`)
        this.assignVisible = false
        this.loadData()
      }).finally(() => {
        this.submitting = false
      })
    },
    handleArrive(row) {
      this.arriveRow = row
      this.arriveTime = ''
      this.arriveVisible = true
    },
    submitArrive() {
      if (!this.arriveTime) {
        this.$message.warning('请选择到达时间')
        return
      }
      if (this.submitting) return
      this.submitting = true
      arriveAppointment(this.arriveRow.id, { arrive_time: this.arriveTime }).then(res => {
        if (res.code !== 200) return
        this.$message.success('已补录到达时间')
        this.arriveVisible = false
        this.loadData()
      }).finally(() => {
        this.submitting = false
      })
    },
    handleMore(cmd, row) {
      if (cmd === 'arrive') {
        this.handleArrive(row)
        return
      }
      const map = {
        finish: { status: 2, label: '标记预约完成' },
        end: { status: 3, label: '结束行程' }
      }
      if (cmd === 'cancel') {
        this.$confirm(`确认取消「${row.name}」${row.appoint_date} 的预约？取消后记录保留，可查不可恢复。`, '取消预约', { type: 'warning' })
          .then(() => {
            this.updateStatus(row, 4, '已取消该预约')
          }).catch(() => {})
        return
      }
      const hit = map[cmd]
      if (!hit) return
      // 前端先挡一道，后端仍会校验流转合法性
      const from = hit.status === 2 ? 1 : 2
      if (row.status !== from) {
        this.$message.warning('当前状态无法执行该操作')
        return
      }
      this.updateStatus(row, hit.status, '已完成：' + hit.label)
    },
    updateStatus(row, status, successText) {
      if (this.submitting) return
      this.submitting = true
      setAppointmentStatus(row.id, status).then(res => {
        // 非法流转由后端返回中文提示（code!==200 时拦截器已弹错误）
        if (res.code !== 200) return
        this.$message.success(successText)
        this.loadData()
      }).finally(() => {
        this.submitting = false
      })
    },
    handleExport() {
      this.$message.info('导出功能待接入，敬请期待')
    }
  }
}
</script>

<style scoped>
/* 行距疏朗：默认 12px 偏挤 */
::v-deep .el-table th {
  padding: 10px 0;
}
::v-deep .el-table td {
  padding: 15px 0;
}
.order-no {
  color: var(--primary, #8E1103);
  font-weight: 600;
}
.cell-sub {
  margin-left: 6px;
  color: #606266;
}
.cell-muted {
  margin-left: 6px;
  color: #a8a29a;
  font-size: 12px;
}
.principal {
  font-weight: 600;
}
.staff-cell i {
  margin-right: 2px;
  color: #909399;
}
.more-btn {
  padding: 0;
}
/* 分配弹框 */
.assign-order {
  padding: 12px 14px;
  border-radius: 6px;
  background: #faf7f1;
  font-size: 13px;
  color: #606266;
}
.ao-row {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.9;
}
.ao-label {
  flex: 0 0 40px;
  color: #909399;
}
.staff-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}
.staff-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #f4f1ec;
  cursor: pointer;
}
.staff-item:last-child {
  border-bottom: none;
}
.staff-item:hover {
  background: #fdfaf6;
}
.staff-item.is-active {
  background: #fdf3f1;
  box-shadow: inset 3px 0 0 var(--primary, #8E1103);
}
.staff-avatar {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--primary, #8E1103);
  color: #fff;
  font-size: 13px;
  line-height: 32px;
  text-align: center;
}
.staff-main {
  flex: 1;
  min-width: 0;
}
.staff-name {
  font-size: 13px;
  color: #303133;
  font-weight: 600;
}
.staff-name .el-tag {
  margin-left: 6px;
  font-weight: 400;
}
.staff-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #a8a29a;
}
.staff-load {
  flex: 0 0 auto;
  font-size: 12px;
}
.load-on {
  color: #d48806;
}
.load-off {
  color: #67c23a;
}
.staff-empty {
  padding: 26px 0;
  text-align: center;
  color: #a8a29a;
  font-size: 13px;
}
.arrive-tip {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 6px;
  background: #faf7f1;
  font-size: 13px;
  color: #606266;
}
.arrive-note {
  margin-top: 4px;
  font-size: 12px;
  color: #a8a29a;
}
.arrive-note i {
  color: #b8ac9c;
  margin-right: 4px;
}
</style>
